import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';

import useErrorManager from "../../hooks/useErrorManager";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

import { Button, Box, Typography, MenuItem, TextField, FormControlLabel, Switch } from "@mui/material";

import PageContainer from '../Layout/PageContainer';
import GenericScanHeader from '../Layout/GenericScanHeader';

import { ctModes } from '../../constants/ctModes';

import { useNotification } from '../../contexts/NotificationContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext'; // Import breadcrumb context


const MeterScheduleScan = () => {
    const { gatewayId } = useParams();
    
    const { setError, clearError, getError } = useErrorManager();
    const { showNotification } = useNotification();
    const { updateBreadcrumb } = useBreadcrumb(); // Use breadcrumb context to update breadcrumb
    
    const navigate = useNavigate();
    const axiosInstance = useAxiosPrivate();

    const [scanDetails, setScanDetails] = useState({
        // temporaryForwardingProfileId: 0,
        description: "",
        ctMode: "C1T1",
        usePlantfile: false,
        setAsPlantfile: false,
        rightnow: false,
        aggregateResults: true,
        time: new Date().toISOString(),
        duration: 940,
    });

    const [loading, setLoading] = useState(true);
    const [details, setDetails] = useState(null);

    useEffect(() => {
        const fetchGatewayDetails = async () => {
            try {
                const response = await axiosInstance.get(`/gateways/${gatewayId}/details-tariff`);
                setDetails(response.data);
                setScanDetails((prev) => ({
                    ...prev,
                    description: `${response.data.name} Meters Scan` || "",
                }));
                clearError('fetchGwDetails');
                setLoading(false);

                updateBreadcrumb([
                    { label: 'Manage Gateways', link: '/gateways' },
                    { label: response.data.name || 'Gateway', link: `/gateways/${gatewayId}` }, // Dynamically set location name
                    { label: 'Meters Scan', link: null }
                ]);

            } catch (error) {
                setError('fetchGwDetails', error.message);
                setLoading(false);
            }
        };
        
        fetchGatewayDetails();
    }, [gatewayId]);

    const handleScanDetailsChange = (field, value) => {
        setScanDetails((prev) => ({
            ...prev,
            [field]: value, // Dynamically update the specified field
        }));
    };

    const handleCreate = async () => {
        try {
            console.log(scanDetails);
            await axiosInstance.put(`/meters/scan/${gatewayId}/plan`, scanDetails);
            clearError('createScan');
            showNotification('Meters scan planned successfully', 'success');
            navigate(`/gateways/${gatewayId}`);
        } catch (error) {
            setError('createScan', "There was an error scheduling the network scan. Please try again later.");
        }
    };

    const handleCancel = () => {
        navigate(`/gateways/${gatewayId}`);
    };

    const infoRows = [
        { key: "duration", label: "Duration", render: (item) =>  `${Math.floor(item.duration/60)} Minutes`   },
        { key: "nextConn", label: "Next Gateway Connection", 
            render: (item) =>  
                    item.gwDetails?.nextConnection 
                ? 
                    new Intl.DateTimeFormat('it-EU', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric', 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: false 
                    }).format(new Date(item.gwDetails.nextConnection))
                : 'unknown',  
        },
        { key: "metersScan", label: "Meters Scans", render: (item) => `${item.gwDetails.tariffInfo.usedDMScansPerYear} used (Max ${item.gwDetails.tariffInfo.dmScansPerYear})` },
    ];

    return (
        <PageContainer>
            {!loading && details ? (
                <>
                    <GenericScanHeader
                        scanDetails={{...scanDetails, gwDetails: details}}
                        infoRows={infoRows}
                        onScanDetailsChange={handleScanDetailsChange}
                    />

                    <TextField
                        label="Mode"
                        select
                        value={scanDetails.ctMode}
                        onChange={(e) => handleScanDetailsChange('ctMode', e.target.value)}
                        fullWidth
                        margin="normal"
                        >
                        {Object.entries(ctModes).map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                            {label}
                        </MenuItem>
                        ))}
                    </TextField>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={scanDetails.setAsPlantfile}
                                onChange={(e) => handleScanDetailsChange('setAsPlantfile', e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Use scan result as new meters list"
                    />

                    <Box display="flex" justifyContent="flex-start" gap={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreate}
                            disabled={
                                !scanDetails.rightnow && (
                                    new Date(scanDetails.time) < new Date()|| 
                                    new Date(scanDetails.time) < new Date(details?.nextConnection || new Date())
                                )
                            }
                        >
                            Create
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                    </Box>
                    {getError('createScan') && <Typography color="error" variant="title2">{getError('createScan')}</Typography>}
                </>
            ) : (
                <p>Loading...</p>
            )}
        </PageContainer>
    );
};

export default MeterScheduleScan;
